import {
  AgenticLogicSuiteGateDetailsSchema,
  type AgenticLogicSuiteTestFailure,
  type AgenticLogicSuiteTestOutcome,
} from "@intelligems/ig-types/dtos/agentic-logic/publish";

import type {
  WorkspaceGateResult,
  WorkspaceSuiteTest,
  WorkspaceTestLineVerdict,
} from "./types";

/**
 * Per-test pass/fail for the editor's gutter, read off a suite gate.
 *
 * A suite gate that records `detailsJson.outcomes` — one `{ testName, state }`
 * per test the run saw, `state` being passed, failed or skipped — is exact:
 * each located test gets the state the run gave it, a failed one with the
 * message from `failures`, and a test the run never saw gets no mark.
 *
 * A gate without `outcomes` is judged from what it does record: the suite's
 * status plus, on failure, `detailsJson.failures` — `{ testName, message }`
 * per failed test, where `testName` is Vitest's full name (`describe > test`).
 * So a PASSED gate means every test passed, and a gate that failed with
 * `reason: "suite_failed"` means every test NOT in `failures` passed. Any
 * other failure (a suite that did not load, a non-deterministic suite) says
 * nothing about individual tests beyond the ones it names.
 */

/** A test the gutter can mark: where it is and Vitest's full name for it. */
export interface SuiteTestLocation {
  line: number;
  title: string;
  /** `[...describePath, title].join(" > ")`, exactly as Vitest reports it. */
  fullName: string;
}

const FULL_NAME_SEPARATOR = " > ";

const fullNameOf = (describePath: readonly string[], title: string): string =>
  [...describePath, title].join(FULL_NAME_SEPARATOR);

export const inventoryLocations = (
  tests: readonly WorkspaceSuiteTest[]
): SuiteTestLocation[] =>
  tests.map((test) => ({
    line: test.startLine,
    title: test.title,
    fullName: fullNameOf(test.describePath, test.title),
  }));

// `describe("x"`, `test("x"`, `it('x'`, `test.skip(\`x\``… A string-literal
// title only: a computed title has no text to match a failure against.
const SUITE_CALL =
  /^\s*(describe|suite|test|it)(?:\.\w+)*\(\s*(["'`])((?:\\.|(?!\2).)*)\2/;

const braceDelta = (line: string): number => {
  let delta = 0;
  for (const char of line) {
    if (char === "{") {
      delta += 1;
    } else if (char === "}") {
      delta -= 1;
    }
  }
  return delta;
};

/**
 * Locates `test(...)` calls, with the describe path each sits under, in a
 * suite the server has not inventoried — the user's own file, which changes on
 * every keystroke. The full name it builds is the same key the server
 * inventory uses, so failures match by name, never by title alone.
 *
 * Lightweight on purpose: line-based and brace-counted rather than parsed. A
 * `describe` opened and closed on one line, or braces inside a string on a
 * structure line, can misplace the path; such a test simply gets no mark
 * unless its misplaced name happens to match.
 */
export const scanSuiteTestLocations = (source: string): SuiteTestLocation[] => {
  const locations: SuiteTestLocation[] = [];
  const open: { title: string; depth: number }[] = [];
  let depth = 0;

  source.split("\n").forEach((text, index) => {
    const match = SUITE_CALL.exec(text);
    if (match) {
      const [, kind, , title] = match;
      if (kind === "describe" || kind === "suite") {
        open.push({ title, depth });
      } else {
        locations.push({
          line: index + 1,
          title,
          fullName: fullNameOf(
            open.map((entry) => entry.title),
            title
          ),
        });
      }
    }
    depth += braceDelta(text);
    while (open.length > 0 && depth <= open[open.length - 1].depth) {
      open.pop();
    }
  });

  return locations;
};

const { failures: FailuresSchema, outcomes: OutcomesSchema } =
  AgenticLogicSuiteGateDetailsSchema.shape;

const failuresOf = (
  details: Record<string, unknown>
): AgenticLogicSuiteTestFailure[] =>
  FailuresSchema.safeParse(details.failures).data ?? [];

/**
 * The per-test outcomes a gate recorded, by Vitest full name, or `null` for a
 * gate that recorded none (or something that is not the contract's shape).
 */
const outcomesOf = (
  details: Record<string, unknown>
): Map<string, AgenticLogicSuiteTestOutcome["state"]> | null => {
  const outcomes = OutcomesSchema.safeParse(details.outcomes).data;
  return outcomes === undefined
    ? null
    : new Map(outcomes.map((outcome) => [outcome.testName, outcome.state]));
};

export const suiteGateVerdicts = (
  gate: WorkspaceGateResult<string> | undefined,
  locations: readonly SuiteTestLocation[]
): WorkspaceTestLineVerdict[] => {
  if (gate === undefined) {
    return [];
  }

  // A finished gate with exact outcomes needs no inference, whatever its
  // status: a passed suite can hold skipped tests, and a failed one names
  // every state the run saw.
  const outcomes =
    (gate.status === "passed" || gate.status === "failed") && gate.detailsJson
      ? outcomesOf(gate.detailsJson)
      : null;
  if (outcomes !== null) {
    const failures = gate.detailsJson ? failuresOf(gate.detailsJson) : [];
    return locations.flatMap(
      ({ line, title, fullName }): WorkspaceTestLineVerdict[] => {
        const state = outcomes.get(fullName);
        if (state === undefined) {
          return [];
        }
        if (state === "failed") {
          const failure = failures.find(
            (candidate) => candidate.testName === fullName
          );
          return [
            {
              line,
              title,
              verdict: {
                status: "failed",
                message: failure?.message ?? "Failed",
              },
            },
          ];
        }
        return [{ line, title, verdict: { status: state } }];
      }
    );
  }

  if (gate.status === "passed") {
    return locations.map(({ line, title }) => ({
      line,
      title,
      verdict: { status: "passed" },
    }));
  }

  if (gate.status !== "failed" || !gate.detailsJson) {
    return [];
  }
  const failures = failuresOf(gate.detailsJson);
  const everyOtherTestPassed = gate.detailsJson.reason === "suite_failed";

  return locations.flatMap(
    ({ line, title, fullName }): WorkspaceTestLineVerdict[] => {
      // By full name, not the inventory id: the id's " #2" suffix for a
      // repeated name is not in what Vitest reports, and two tests with one
      // full name are one name to Vitest too, so a failure marks both.
      const failure = failures.find(
        (candidate) => candidate.testName === fullName
      );
      if (failure) {
        return [
          {
            line,
            title,
            verdict: { status: "failed", message: failure.message },
          },
        ];
      }
      return everyOtherTestPassed
        ? [{ line, title, verdict: { status: "passed" } }]
        : [];
    }
  );
};
